import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import {
  IconHome,
  IconHomeFill,
  IconTrashCanSimple,
  IconSquareCheck,
  IconSquareCheckFill,
  IconBubbles,
  IconBubblesFill,
  IconPageCheck,
  IconPageCheckFill,
  IconTrending5,
  IconTrending4Fill,
  IconPeopleCircle,
  IconPeopleCircleFill,
  IconMapPin2,
  IconGlobe,
  IconArrowBoxLeft,
  IconClipboard,
  IconClipboardFill,
} from 'src/ui-new/icons-local/generated-central';
import { T, useT } from '@transifex/react';
import { CompanyContext } from 'src/context/company-context';
import { PropertyContext } from 'src/context/property-context';
import { useUser } from 'src/app/User.store';
import { useCompany } from 'src/app/Company.store';
import { useCapabilities } from 'src/app/capabilities';
import { useRoleChecker, useRouting } from 'src/routing/child-router-factory';
import { useUnreadCount } from 'src/api/messages/hooks';

import { createNavData } from 'src/components/layout/sidebar/navData';
import { isAccountSectionEnabled } from 'src/layouts/accountNavGating';
import { useLinkedPrincipal } from 'src/app/useLinkedPrincipal';
import { RoleBadge } from 'src/components/shared/RoleBadge';
import { PrincipalSwitcher } from 'src/components/principal/PrincipalSwitcher';
import {
  LanguagePickerModal,
  useHasMultipleLanguages,
} from 'src/components/login/LanguagePicker';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type NavIcon = React.ComponentType<
  React.SVGProps<SVGSVGElement> & { size?: number }
>;

// Keyed by nav `key` (see navData.tsx), matching the Portal Figma sidebar
// (node 257-4834). Each item has a `line` and `fill` variant; the nav renders
// `line` in both states (active recolors only) — `fill` is kept wired for an
// easy swap back.
type NavIconPair = { line: NavIcon; fill: NavIcon };
const NAV_ICONS: Record<string, NavIconPair> = {
  'self-service': { line: IconHome, fill: IconHomeFill }, // Overview
  // Emptyings (Tømninger) — Portal Figma uses IconTrashCanSimple (node
  // 332:754). Only one variant in the design, so fill reuses the line icon.
  collections: { line: IconTrashCanSimple, fill: IconTrashCanSimple },
  services: { line: IconSquareCheck, fill: IconSquareCheckFill }, // Services
  messages: { line: IconBubbles, fill: IconBubblesFill }, // Messages
  agreements: { line: IconPageCheck, fill: IconPageCheckFill }, // Agreements
  reports: { line: IconTrending5, fill: IconTrending4Fill }, // Reports
  account: { line: IconPeopleCircle, fill: IconPeopleCircleFill }, // Account
  composting: { line: IconClipboard, fill: IconClipboardFill },
};

const FALLBACK_ICON: NavIconPair = {
  line: IconHome,
  fill: IconHomeFill,
};

// Unread-count pill on a nav item (currently only Messages). Inverts against
// the active row (navy bg) so the count stays legible in both states.
// Exported for tests.
export function NavUnreadBadge({
  count,
  isActive,
  label,
}: {
  count: number;
  isActive: boolean;
  label: string;
}) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={label}
      className={cn(
        'ml-auto inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1.5 text-[13px] font-semibold leading-none tabular-nums',
        isActive
          ? 'bg-white text-[var(--brand-navy)]'
          : 'bg-[var(--brand-navy)] text-white'
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Gate the entrance animation to "once per page load". `performance.timeOrigin`
// is constant for the document's lifetime and changes on every fresh load —
// stamping it into sessionStorage and comparing on next mount tells us whether
// we've already animated *in this lifecycle*. Survives module re-evaluation
// (HMR), React StrictMode, and any in-app remount; resets only on a real page
// reload, which is exactly the desired UX.
//
// The animation itself is triggered imperatively via Web Animations API in a
// layout effect — not via a CSS class. CSS class-based animations replay
// whenever the class lands on a fresh DOM element, which is what was happening
// on tab focus (some upstream rerender was re-creating the elements). The
// imperative approach plays exactly when we tell it to, regardless of how
// React shuffles the DOM.
const SIDEBAR_ANIMATED_ORIGIN_KEY = 'wh-sidebar-animated-origin';
const STAGGER_ATTR = 'data-wh-sidebar-stagger';

function shouldAnimateSidebarEntrance(): boolean {
  try {
    return (
      sessionStorage.getItem(SIDEBAR_ANIMATED_ORIGIN_KEY) !==
      String(performance.timeOrigin)
    );
  } catch {
    return true;
  }
}

function markSidebarEntranceAnimated(): void {
  try {
    sessionStorage.setItem(
      SIDEBAR_ANIMATED_ORIGIN_KEY,
      String(performance.timeOrigin)
    );
  } catch {
    /* sessionStorage unavailable (private mode, disabled) — no-op */
  }
}

export default function PortalSidebar() {
  const { logo } = useContext(CompanyContext);
  const showLogo = !window.WH_SS_NO_LOGO && logo;

  const [checkRole, { routingMap }] = useRoleChecker();
  const history = useRouting.useHistory();
  const { pathname } = useLocation();
  const tx = useT();
  const { data: user, logout } = useUser();
  const property = useContext(PropertyContext);
  const { role, can } = useCapabilities();
  const { data: companyData } = useCompany();
  const linkedPrincipal = useLinkedPrincipal();
  // See `isAccountSectionEnabled` for why the flags AND the principal matter.
  const accountEnabled = isAccountSectionEnabled({
    accountOverviewFlag: companyData?.crm_enable_account_overview,
    commsPreferencesFlag: companyData?.crm_enable_comms_preferences,
    principal: linkedPrincipal,
  });
  const reportsEnabled = !!companyData?.crm_enable_reports;
  // "Beskeder" hub gathers cases (Sager) and conversations under one nav item,
  // and `crm_enable_messaging` is the single gate for all of it: turning the
  // tenant setting off silences two-way messaging, so the hub — Sager tab
  // included — disappears with it. The route redirects to match (see
  // routes/messages/Messages.tsx).
  const beskederEnabled = !!companyData?.crm_enable_messaging;
  const compostingEnabled = !!companyData?.crm_enable_composting;
  const displayName = user?.anonymous ? null : user?.name;
  const addressLine = property?.location?.name;

  // Unread badge on the Messages nav item. Gated on the messaging flag so
  // tenants without conversations never hit the endpoint.
  const unreadQ = useUnreadCount(property?.id ?? '', {
    enabled: !!companyData?.crm_enable_messaging,
  });
  const unreadCount = unreadQ.data?.count ?? 0;

  const navData = useMemo(
    () =>
      createNavData(routingMap, {
        accountEnabled,
        beskederEnabled,
        reportsEnabled,
        compostingEnabled,
      }),
    [
      routingMap,
      accountEnabled,
      beskederEnabled,
      reportsEnabled,
      compostingEnabled,
    ]
  );

  const items = useMemo(
    () =>
      navData
        .filter(
          (item: $TSFixMe) =>
            (!item.roles || checkRole(item.roles)) &&
            (!item.capability || can(item.capability))
        )
        .map((item: $TSFixMe) => ({
          key: item.key as string,
          title: item.title as React.ReactNode,
          link: item.link as string,
          icons: NAV_ICONS[item.key as string] ?? FALLBACK_ICON,
        })),
    [navData, checkRole, can]
  );

  const activeKey = useMemo(() => {
    let bestKey = '';
    let bestLen = 0;
    items.forEach(({ key, link }) => {
      if (!link) return;
      const m = matchPath({ path: link, end: false }, pathname);
      if (m && link.length > bestLen) {
        bestKey = key;
        bestLen = link.length;
      }
    });
    return bestKey;
  }, [items, pathname]);

  const [animateEntrance] = useState(shouldAnimateSidebarEntrance);
  const [isLangModalOpen, setIsLangModalOpen] = useState(false);
  const hasMultipleLanguages = useHasMultipleLanguages();
  const asideRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!animateEntrance) return;
    markSidebarEntranceAnimated();
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const root = asideRef.current;
    if (!root) return;
    const staggerEls = root.querySelectorAll<HTMLElement>(`[${STAGGER_ATTR}]`);
    if (!staggerEls.length) return;
    staggerEls.forEach((el, i) => {
      el.animate(
        [
          { opacity: 0, transform: 'translateX(-12px)' },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration: 300, easing: 'ease-out', delay: i * 60, fill: 'both' }
      );
    });
  }, [animateEntrance]);

  // Document-order querySelectorAll picks up these `[data-wh-sidebar-stagger]`
  // elements top-to-bottom and the layout effect animates them with index-based
  // delay. Conditional sections (logo / user / address) drop out cleanly, no
  // index bookkeeping required.
  return (
    <aside
      ref={asideRef}
      className="wh-ui flex h-full w-full flex-col justify-between bg-[var(--wh-app-bg)] p-8"
    >
      <div className="flex flex-col gap-6">
        {showLogo && (
          <div data-wh-sidebar-stagger className="flex items-start">
            <img src={logo} alt="" className="h-auto w-full object-contain" />
          </div>
        )}
        <div className="flex flex-col gap-6">
          {displayName && (
            <div
              data-wh-sidebar-stagger
              className="flex items-center gap-4 text-[16px] leading-[1.5] text-[var(--wh-sidebar-fg)]"
            >
              <IconPeopleCircle
                size={24}
                aria-hidden
                className="shrink-0 text-[var(--wh-sidebar-fg)]"
              />
              <span>{displayName}</span>
            </div>
          )}
          {addressLine && (
            <div
              data-wh-sidebar-stagger
              className="flex items-center gap-4 text-[16px] leading-[1.5] text-[var(--wh-sidebar-fg)]"
            >
              <IconMapPin2
                size={24}
                aria-hidden
                className="shrink-0 text-[var(--wh-sidebar-fg)]"
              />
              <span className="whitespace-pre-line">{addressLine}</span>
            </div>
          )}
          {role && (
            <div data-wh-sidebar-stagger>
              <RoleBadge role={role} />
            </div>
          )}
          <div data-wh-sidebar-stagger>
            <PrincipalSwitcher />
          </div>
        </div>
      </div>

      <nav
        aria-label={tx('Primary')}
        className="flex flex-1 flex-col justify-center gap-1"
      >
        {items.map(({ key, title, link, icons }) => {
          const isActive = key === activeKey;
          const Icon = icons.line;
          return (
            <button
              key={key}
              type="button"
              data-wh-sidebar-stagger
              aria-current={isActive ? 'page' : undefined}
              onClick={() => link && history.push(link)}
              className={cn(
                'flex h-12 w-full items-center gap-4 rounded-[var(--wh-radius,8px)] px-4 text-left text-[16px] font-medium leading-[1.5] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
                isActive
                  ? 'bg-[var(--brand-navy)] text-white'
                  : 'text-[var(--wh-sidebar-fg)] hover:bg-[var(--wh-sidebar-hover)]'
              )}
            >
              <Icon
                size={24}
                aria-hidden
                className={cn(
                  'shrink-0',
                  isActive ? 'text-white' : 'text-[var(--wh-sidebar-fg)]'
                )}
              />
              <span>{title}</span>
              {key === 'messages' && (
                <NavUnreadBadge
                  count={unreadCount}
                  isActive={isActive}
                  label={tx('{count} unread', { count: unreadCount })}
                />
              )}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-col gap-1">
        <div data-wh-sidebar-stagger>
          {hasMultipleLanguages && (
            <button
              type="button"
              onClick={() => setIsLangModalOpen(true)}
              className="flex h-12 w-full items-center gap-4 rounded-[var(--wh-radius,8px)] border border-[var(--wh-sidebar-border)] bg-[var(--wh-sidebar-surface)] px-4 text-[16px] font-medium leading-[1.5] text-[var(--wh-sidebar-fg)] transition-colors hover:bg-[var(--wh-sidebar-hover)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]"
            >
              <IconGlobe className="size-6" aria-hidden />
              <T _str="Change language" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          onClick={logout}
          data-wh-sidebar-stagger
          className="h-12 w-full justify-start gap-4 rounded-[var(--wh-radius,8px)] border-[var(--wh-sidebar-border)] bg-[var(--wh-sidebar-surface)] px-4 text-[16px] font-medium leading-[1.5] text-[var(--wh-sidebar-fg)] shadow-none hover:bg-[var(--wh-sidebar-hover)] hover:text-[var(--wh-sidebar-fg)] has-[>svg]:px-4"
        >
          <IconArrowBoxLeft className="size-6" aria-hidden />
          <T _str="Log out" />
        </Button>
      </div>
      {hasMultipleLanguages && (
        <LanguagePickerModal
          open={isLangModalOpen}
          onOpenChange={setIsLangModalOpen}
        />
      )}
    </aside>
  );
}
